import { create } from 'zustand';
import {
  deriveKeys as deriveKeysUtil,
  encrypt as encryptUtil,
  decrypt as decryptUtil,
  generateSalt as generateSaltUtil,
  generatePassword as generatePasswordUtil,
  calculateStrength,
  generateShareToken as generateShareTokenUtil,
  encryptKeyWithToken as encryptKeyWithTokenUtil,
  decryptKeyWithToken as decryptKeyWithTokenUtil,
  exportKey as exportKeyUtil,
  importKey as importKeyUtil,
  deriveHmacKey,
  computeHmac as computeHmacUtil,
} from '@/lib/crypto';
import { CONFIG } from '@/constants/config';

const SESSION_KEY = 'op_ek';

// Restore encryption key from sessionStorage on page refresh
function restoreKeyFromSession(): { key: Uint8Array; ready: boolean } {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      return { key: importKeyUtil(stored), ready: true };
    }
  } catch {
    // sessionStorage not available or corrupted
  }
  return { key: null as unknown as Uint8Array, ready: false };
}

const restored = restoreKeyFromSession();

interface KdfParams {
  iterations: number;
  memory: number;
  parallelism: number;
}

interface DerivedKeys {
  authKeyHash: string;
  encryptionKey: Uint8Array;
  verifier: string;
}

interface PasswordOptions {
  length: number;
  uppercase: boolean;
  lowercase: boolean;
  numbers: boolean;
  symbols: boolean;
}

interface PasswordStrength {
  score: number;
  label: 'very-weak' | 'weak' | 'fair' | 'strong' | 'very-strong';
  suggestions: string[];
}

interface CryptoState {
  encryptionKey: Uint8Array | null;
  isReady: boolean;

  setEncryptionKey: (key: Uint8Array) => void;
  clearEncryptionKey: () => void;

  deriveKeys: (
    password: string,
    salt: string,
    params?: KdfParams
  ) => Promise<DerivedKeys>;
  encrypt: (plaintext: string, key?: Uint8Array) => Promise<string>;
  decrypt: (ciphertext: string, key?: Uint8Array) => Promise<string>;
  generateSalt: () => string;
  generatePassword: (options: PasswordOptions) => string;
  checkPasswordStrength: (password: string) => PasswordStrength;

  // Vault sharing
  generateShareToken: () => string;
  encryptKeyForSharing: (shareToken: string) => Promise<string>;
  decryptSharedKey: (encryptedKey: string, shareToken: string) => Promise<Uint8Array>;
  exportEncryptionKey: () => string | null;
  importEncryptionKey: (keyBase64: string) => Uint8Array;

  // HMAC for passkey lookups
  computeHmac: (data: string, context?: string) => Promise<string>;
}

export const useCryptoStore = create<CryptoState>()((set, get) => ({
  encryptionKey: restored.ready ? restored.key : null,
  isReady: restored.ready,

  setEncryptionKey: (key) => {
    set({ encryptionKey: key, isReady: true });
    try {
      sessionStorage.setItem(SESSION_KEY, exportKeyUtil(key));
    } catch {
      // sessionStorage not available
    }
  },

  clearEncryptionKey: () => {
    const { encryptionKey } = get();
    if (encryptionKey) {
      encryptionKey.fill(0);
    }
    set({ encryptionKey: null, isReady: false });
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // sessionStorage not available
    }
  },

  deriveKeys: async (password, salt, params) => {
    const defaultParams: KdfParams = {
      iterations: CONFIG.KDF.ITERATIONS,
      memory: CONFIG.KDF.MEMORY,
      parallelism: CONFIG.KDF.PARALLELISM,
    };

    return deriveKeysUtil(password, salt, params || defaultParams);
  },

  encrypt: async (plaintext, key) => {
    const encKey = key || get().encryptionKey;
    if (!encKey) throw new Error('Encryption key not available');
    return encryptUtil(plaintext, encKey);
  },

  decrypt: async (ciphertext, key) => {
    const encKey = key || get().encryptionKey;
    if (!encKey) throw new Error('Encryption key not available');
    return decryptUtil(ciphertext, encKey);
  },

  generateSalt: () => generateSaltUtil(),

  generatePassword: (options) => generatePasswordUtil(options),

  checkPasswordStrength: (password) => calculateStrength(password),

  // Vault sharing methods
  generateShareToken: () => generateShareTokenUtil(),

  encryptKeyForSharing: async (shareToken) => {
    const encKey = get().encryptionKey;
    if (!encKey) throw new Error('Encryption key not available');
    return encryptKeyWithTokenUtil(encKey, shareToken);
  },

  decryptSharedKey: async (encryptedKey, shareToken) => {
    return decryptKeyWithTokenUtil(encryptedKey, shareToken);
  },

  exportEncryptionKey: () => {
    const encKey = get().encryptionKey;
    if (!encKey) return null;
    return exportKeyUtil(encKey);
  },

  importEncryptionKey: (keyBase64) => {
    return importKeyUtil(keyBase64);
  },

  computeHmac: async (data, context = 'oneckpass:passkey:lookup-v1') => {
    const encKey = get().encryptionKey;
    if (!encKey) throw new Error('Encryption key not available');
    const hmacKey = await deriveHmacKey(encKey, context);
    return computeHmacUtil(hmacKey, data);
  },
}));

export const cryptoStore = useCryptoStore;
